export interface TrafficLightAnimation {
  id: string;
  topic: string;
  sceneDescription: string;
  klingPrompt: string; // original Kling AI prompt, kept for reference
  pikaPrompt: string; // Pika.art positive prompt
  negativePrompt: string; // Pika.art negative prompt
  videoUrl: string | null; // null until generated externally and URL added
  durationSeconds: number;
  viewType: 'first-person' | 'top-down' | 'third-person';
  freezeDescription: string;
  questionIds: string[]; // questions this animation supports
}

export const trafficLightsAnimations: TrafficLightAnimation[] = [
  {
    id: 'tls-anim-1',
    topic: 'traffic-lights-signals',
    sceneDescription:
      'First-person driver POV approaching a Dutch urban traffic light showing red, then amber, then green. Standard Dutch traffic light on yellow pole. Driver waits, light turns green, car begins to move forward.',
    klingPrompt:
      'First-person driver POV, Dutch urban road, standard Dutch traffic light on yellow metal pole ahead showing red. Light sequence: red → amber → green. Driver waits, then begins moving forward as light turns green. Dutch road markings, overcast daylight, photorealistic. 5 seconds.',
    pikaPrompt:
      'Interior driver POV from a stationary car at a Dutch urban junction. Standard Dutch traffic light on a yellow metal pole ahead showing red. Sequence: red holds for 2 seconds, amber appears briefly, then green lights up and car begins to move forward. Dutch grey asphalt road, white stop line, overcast Dutch daylight. Photorealistic, cinematic.',
    negativePrompt:
      'cartoon, CGI, 3D render, anime, UK traffic lights, USA traffic lights, night, rain, fisheye, motion blur, text overlay, watermark',
    videoUrl: null,
    durationSeconds: 5,
    viewType: 'first-person',
    freezeDescription: 'Traffic light turning green — driver about to proceed.',
    questionIds: ['q-tls-2'],
  },
  {
    id: 'tls-anim-2',
    topic: 'traffic-lights-signals',
    sceneDescription:
      'First-person driver POV, car stationary at a Dutch urban junction. Main traffic light shows solid red. A separate green arrow signal panel illuminates (pointing right or left depending on question). A cyclist is visible on the fietspad crossing the path the car would take. Freeze frame with green arrow lit and cyclist mid-crossing.',
    klingPrompt:
      'First-person driver POV, car stationary at a Dutch junction, standard Dutch traffic light showing solid red. A separate green arrow signal panel lights up pointing to the right. Visible in the scene: Dutch-style junction, white stop line, green cycle lane (fietspad) crossing on the right side, a cyclist mid-crossing from left to right. Camera holds steady, slight idle vibration. Freeze frame with green arrow illuminated and cyclist clearly visible in right third of frame. Realistic, Dutch urban daytime, overcast, no sound. 4 seconds total.',
    pikaPrompt:
      'Interior driver POV from a stationary car at a Dutch urban junction, engine idling. Standard Dutch traffic light on a yellow metal pole ahead showing solid red. Directly below it a separate rectangular green arrow signal panel illuminates pointing right. White stop line just in front of the car. Red-surfaced fietspad (cycle path) crosses in front of the car from the right. A single cyclist in a rain jacket and Dutch-style helmet crosses left to right on the fietspad, mid-crossing. Window glass reflections visible. Camera completely static, slight engine idle vibration. Overcast Dutch daytime. Photorealistic, cinematic quality.',
    negativePrompt:
      'cartoon, CGI, 3D render, anime, moving car, UK traffic lights, USA traffic lights, multiple cyclists, pedestrians in road, night, rain, fisheye, motion blur, tram tracks, roundabout, sunny sky, text overlay, watermark, blur, pedestrian crossing, green main light',
    videoUrl: null,
    durationSeconds: 4,
    viewType: 'first-person',
    freezeDescription: 'Green arrow lit, cyclist mid-crossing — give-way decision moment.',
    questionIds: ['q-tls-9'],
  },
  {
    id: 'tls-anim-3',
    topic: 'traffic-lights-signals',
    sceneDescription:
      'First-person driver POV on a Dutch rural road approaching a spoorwegovergang (level crossing). No barriers down yet. The amber warning light flashes twice, then the twin red lights begin alternating. No train visible in either direction. Freeze frame at the moment red lights are actively flashing with crossing still open.',
    klingPrompt:
      'First-person driver POV, Dutch rural road approaching a level crossing (spoorwegovergang). Road is straight, clear asphalt. Level crossing signal mast visible ahead with amber warning light. Sequence: amber light flashes twice, then twin red lights begin alternating left-right flash. No train visible on tracks in either direction. No barrier arm visible yet. Freeze frame at moment red lights are actively flashing, crossing still clear. Dutch countryside, overcast daylight, no sound, realistic style. 4–5 seconds total.',
    pikaPrompt:
      'Dashcam driver POV approaching a Dutch railway level crossing (spoorwegovergang) on a straight flat rural road. Flat Dutch polder landscape with open green fields and low horizon on both sides. Level crossing warning signal mast visible ahead with a round amber warning light. Sequence: amber light flashes twice, then two round red warning lights on the mast begin alternating left-right repeatedly. Railway tracks visible crossing the road ahead. No train visible in either direction. No barrier arm visible yet. Slow steady dolly-forward approach, smooth motion. Overcast grey Dutch sky. Photorealistic, cinematic quality.',
    negativePrompt:
      'cartoon, CGI, 3D render, anime, train visible, barriers lowering, UK level crossing, USA railroad crossing, night, fog, heavy rain, fisheye, motion blur, urban street, highway, motorway, tram, text overlay, watermark, busy traffic, pedestrians, barriers down',
    videoUrl: null,
    durationSeconds: 5,
    viewType: 'first-person',
    freezeDescription: 'Twin red lights flashing, no train visible, crossing still open — stop or go decision.',
    questionIds: ['q-tls-8'],
  },
  {
    id: 'tls-anim-4',
    topic: 'traffic-lights-signals',
    sceneDescription:
      'First-person driver POV approaching a signalised pedestrian crossing. The driver\'s traffic light is green. An older adult pedestrian has just stepped onto the zebra crossing from the left pavement, walking slowly, one-third of the way across. The pedestrian signal on the far side shows a red standing figure. Car is approximately 15 metres from the crossing. Freeze: pedestrian mid-crossing, driver light green.',
    klingPrompt:
      'First-person driver POV, Dutch urban road approaching a signalised pedestrian crossing (voetgangersoversteekplaats). The traffic light ahead for the driver shows green. An older adult pedestrian has just stepped onto the zebra crossing from the left, walking slowly, approximately one-third of the way across. The pedestrian signal post on the far side shows a red standing figure. The car is approximately 15 metres from the crossing and moving at low speed. Freeze frame with pedestrian clearly mid-crossing and driver light showing green. Dutch-style yellow bollard crossing markers visible. Realistic, daytime, no sound. 4 seconds total.',
    pikaPrompt:
      'Driver POV from a slow-moving car approaching a Dutch signalised pedestrian crossing (zebrapad). Dutch traffic light ahead on a yellow pole showing green for the driver. White zebra stripes painted on the road surface. Bright yellow pedestrian crossing bollards on both kerbs. Elderly woman approximately 70 years old wearing a long winter coat and carrying a handbag, walking slowly from the left side of the crossing, she is about one-third of the way across. Pedestrian signal post on the far side showing a red standing-person icon. Car approaching at low speed, approximately 15 metres from the crossing. Smooth slow dolly-forward. Overcast Dutch urban daylight. Photorealistic, cinematic quality.',
    negativePrompt:
      'cartoon, CGI, 3D render, anime, young pedestrian, child, cyclist on crossing, night, rain, UK crossing, USA crossing, fisheye, motion blur, pedestrian running, green pedestrian signal, fast moving car, crowd, multiple pedestrians, text overlay, watermark, sunny sky',
    videoUrl: null,
    durationSeconds: 4,
    viewType: 'first-person',
    freezeDescription: 'Pedestrian mid-crossing, driver green — give-way decision moment.',
    questionIds: ['q-tls-19'],
  },
  {
    id: 'tls-anim-5',
    topic: 'traffic-lights-signals',
    sceneDescription:
      'First-person driver POV on a straight Dutch urban road. A large white Dutch city bus is parked at a bus stop (HALTE) on the right side. The bus has a blue HALTE stop sign. The bus right indicator light is flashing orange as it slowly pulls out into the traffic lane. The driver must recognize bus priority and give way. Freeze frame with orange indicator clearly visible and bus beginning to merge.',
    klingPrompt:
      'Dashcam driver POV from a car driving on a straight Dutch urban road. A large white Dutch city bus is parked at a bus stop on the right side of the road. The bus stop has a blue Dutch halte sign. The bus right indicator light is flashing orange. The bus is slowly pulling out from the bus stop into the lane. Dutch road: grey asphalt, 50 km/h speed sign on a pole on the right side, white road markings, Dutch brick houses on both sides. Overcast Dutch daylight. The orange indicator on the bus is clearly visible. Photorealistic, cinematic. 5 seconds.',
    pikaPrompt:
      'Dashcam driver POV from a car driving on a straight Dutch urban road. A large white Dutch city bus is parked at a bus stop on the right side of the road. The bus stop has a blue Dutch halte sign. The bus right indicator light is flashing orange. The bus is slowly pulling out from the bus stop into the lane. Dutch road: grey asphalt, 50 km/h speed sign on a pole on the right side, white road markings, Dutch brick houses on both sides. Overcast Dutch daylight. The orange indicator on the bus is clearly visible. Photorealistic, cinematic. 5 seconds.',
    negativePrompt:
      'cartoon, CGI, 3D render, anime, night, rain, UK roads, USA roads, text overlay, watermark, sunny sky, tram, highway, motorway',
    videoUrl: null,
    durationSeconds: 5,
    viewType: 'first-person',
    freezeDescription: 'Bus pulling out from HALTE stop with indicator — give-way decision moment.',
    questionIds: ['q-tls-5'],
  },
  {
    id: 'tls-anim-6',
    topic: 'traffic-lights-signals',
    sceneDescription: 'Red pedestrian crossing light (red hand symbol) - pedestrians must not cross.',
    klingPrompt: 'Red pedestrian crossing signal with standing hand symbol.',
    pikaPrompt: 'Red pedestrian crossing signal showing red standing hand.',
    negativePrompt: 'green signal, walking figure, pedestrians crossing',
    videoUrl: null,
    durationSeconds: 3,
    viewType: 'first-person',
    freezeDescription: 'Red pedestrian signal - do not cross.',
    questionIds: ['q-tls-6'],
  },
  {
    id: 'tls-anim-7',
    topic: 'traffic-lights-signals',
    sceneDescription: 'Green pedestrian crossing light (green walking figure) - pedestrians may cross.',
    klingPrompt: 'Green pedestrian crossing signal with walking figure.',
    pikaPrompt: 'Green pedestrian crossing signal showing green walking figure.',
    negativePrompt: 'red signal, standing hand, no pedestrians',
    videoUrl: null,
    durationSeconds: 3,
    viewType: 'first-person',
    freezeDescription: 'Green pedestrian signal - pedestrians may cross.',
    questionIds: ['q-tls-7'],
  },
  {
    id: 'tls-anim-8',
    topic: 'traffic-lights-signals',
    sceneDescription: 'Orange indicator light on vehicle ahead showing turn/lane change signal.',
    klingPrompt: 'Orange indicator/blinker light on vehicle ahead.',
    pikaPrompt: 'Orange indicator light flashing on vehicle.',
    negativePrompt: 'red lights, brake lights, no signal',
    videoUrl: null,
    durationSeconds: 3,
    viewType: 'first-person',
    freezeDescription: 'Vehicle indicator light - turn/lane change signal.',
    questionIds: ['q-tls-10'],
  },
  {
    id: 'tls-anim-9',
    topic: 'traffic-lights-signals',
    sceneDescription: 'Flashing amber light on traffic signal - signal not fully operational.',
    klingPrompt: 'Flashing amber traffic light signal.',
    pikaPrompt: 'Amber traffic light flashing.',
    negativePrompt: 'solid lights, red, green, working signal',
    videoUrl: null,
    durationSeconds: 3,
    viewType: 'first-person',
    freezeDescription: 'Flashing amber signal.',
    questionIds: ['q-tls-14'],
  },
  {
    id: 'tls-anim-10',
    topic: 'traffic-lights-signals',
    sceneDescription: 'Red X symbol on overhead motorway signal - lane closed.',
    klingPrompt: 'Red X on motorway overhead sign indicating closed lane.',
    pikaPrompt: 'Red X symbol on motorway overhead signal.',
    negativePrompt: 'green light, lane open, arrows',
    videoUrl: null,
    durationSeconds: 3,
    viewType: 'first-person',
    freezeDescription: 'Red X on motorway - lane closed.',
    questionIds: ['q-tls-15'],
  },
  {
    id: 'tls-anim-11',
    topic: 'traffic-lights-signals',
    sceneDescription: 'Amber flashing lights on roadside/barrier - temporary hazard or roadworks.',
    klingPrompt: 'Amber flashing warning lights on roadside barrier.',
    pikaPrompt: 'Amber flashing lights warning of hazard.',
    negativePrompt: 'red lights, emergency lights, green',
    videoUrl: null,
    durationSeconds: 3,
    viewType: 'first-person',
    freezeDescription: 'Amber flashing warning lights.',
    questionIds: ['q-tls-18'],
  },
  {
    id: 'tls-anim-12',
    topic: 'traffic-lights-signals',
    sceneDescription: 'Green arrow traffic signal with turning vehicle.',
    klingPrompt: 'Green arrow traffic signal allowing protected turn.',
    pikaPrompt: 'Green arrow traffic signal.',
    negativePrompt: 'red signal, no arrow, cyclist',
    videoUrl: null,
    durationSeconds: 3,
    viewType: 'first-person',
    freezeDescription: 'Green arrow signal.',
    questionIds: ['q-tls-4'],
  },
  {
    id: 'tls-anim-13',
    topic: 'traffic-lights-signals',
    sceneDescription: 'Red traffic light - driver must stop and wait.',
    klingPrompt: 'Red traffic light signal.',
    pikaPrompt: 'Red traffic light showing.',
    negativePrompt: 'green light, yellow light, moving traffic',
    videoUrl: null,
    durationSeconds: 3,
    viewType: 'first-person',
    freezeDescription: 'Red traffic light - stop.',
    questionIds: ['q-tls-1'],
  },
];
